import {
  signOut,
  useAuth,
  useClaims,
  useSubdomainCheck,
} from '@backoffice-frontend/authentication'
import { CommandPalette } from '@backoffice-frontend/command-palette'
import {
  AccountViewRoute,
  LOGIN_INTENDED_PATH,
  Routes,
  useRefContext,
} from '@backoffice-frontend/common'
import { BackofficeSignInMethod } from '@backoffice-frontend/graphql'
import { NavigationDrawerContextProvider } from '@backoffice-frontend/patterns'
import {
  EndSessionDialog,
  useTemporarySessionId,
} from '@backoffice-frontend/temporary-backoffice-sessions-common'
import { usePageTracking } from '@backoffice-frontend/tracking'
import {
  useBackofficeLoggedInUser,
  useRefreshPermissions,
  useRefreshPermissionsSuccessToast,
} from '@backoffice-frontend/user-management-ui'
import { LoadingIndicator, useDialog } from '@moia-dev/pace-web'
import { type ReactNode, Suspense, useEffect, useRef, useState } from 'react'
import {
  Navigate,
  Route as RouterRoute,
  Routes as RouterRoutes,
  useLocation,
} from 'react-router'
import { useSentryUserContext } from '../useSentryUserContext'
import { useWebSocketMetrics } from '../useWebSocketMetrics'
import { AccountViewArea } from './AccountView'
import { AppLayout } from './AppLayout'
import { SetupMFA } from './MFAView/SetupMFA'
import { NavigationDrawer } from './NavigationDrawer'
import { OfflineBanner } from './OfflineBanner'
import { PageNotFoundView } from './PageNotFoundView'
import {
  TemporarySessionBanner,
  useActiveTemporarySession,
  useHasSessionExpired,
} from './TemporarySessionBanner'
import type { BackofficeAreas } from './types/BackofficeArea'
import type { SectionsConfig } from './types/NavigationSectionsConfig'
import { useGetAreaTranslation } from './useGetAreaTranslation'
import { WrongSubdomainView } from './WrongSubdomainView'

const AreaLoadingFallback = () => (
  <div
    css={theme => ({
      display: 'flex',
      justifyContent: 'center',
      padding: theme.space.Space4,
    })}
  >
    <LoadingIndicator />
  </div>
)

type AuthenticatedAppViewProps = {
  useNavigationSections: () => SectionsConfig
  logo: ReactNode
  logoVariant?: 'dark' | 'light'
}

export const AuthenticatedAppView = (props: AuthenticatedAppViewProps) => {
  const { isAuthenticated, loading } = useAuth()
  const location = useLocation()

  if (loading) {
    return null
  }

  if (!isAuthenticated) {
    const fullPath = `${location.pathname}${location.search}${location.hash}`
    localStorage.setItem(LOGIN_INTENDED_PATH, fullPath)
    return <Navigate to={Routes.Auth.Login.url} replace />
  }

  return <SubdomainGate {...props} />
}

/**
 * The hostname does not scope any request — data is scoped by the token alone.
 * So rendering the app before we know the subdomain belongs to this user would
 * show the previous organization's data under a hostname claiming otherwise.
 * Blocks until confirmed, and blocks when it cannot be confirmed.
 *
 * Skipped entirely inside a temporary Backoffice session: the actor is
 * deliberately impersonating another organization, so its subdomain never
 * matches the impersonating actor's own — that's expected, not a foreign-org
 * access attempt.
 */
const SubdomainGate = (props: AuthenticatedAppViewProps) => {
  const isTemporarySession = !!useTemporarySessionId()
  const subdomainCheck = useSubdomainCheck(isTemporarySession)

  if (subdomainCheck.status === 'checking') {
    return <AreaLoadingFallback />
  }

  if (subdomainCheck.status !== 'match') {
    return (
      <WrongSubdomainView
        expectedUrl={
          subdomainCheck.status === 'mismatch'
            ? subdomainCheck.expectedUrl
            : undefined
        }
      />
    )
  }

  return <AuthenticatedAppViewWrapper {...props} />
}

const AuthenticatedAppViewWrapper = ({
  useNavigationSections,
  logo,
  logoVariant,
}: AuthenticatedAppViewProps) => {
  const { setRef } = useRefContext()
  const appContextRef = useRef(null)
  const { hasMFA } = useAuth()
  const { user } = useBackofficeLoggedInUser()
  useRefreshPermissionsSuccessToast()
  useSentryUserContext()
  useWebSocketMetrics()
  const navigationSections = useNavigationSections()
  const { getAreaTranslation } = useGetAreaTranslation()

  const isTemporarySession = !!useTemporarySessionId()
  const temporarySession = useActiveTemporarySession()
  const hasSessionExpired = useHasSessionExpired(
    temporarySession.session?.expiresAt,
  )
  const endSessionDialog = useDialog()
  const refreshPermissions = useRefreshPermissions()

  // pace-web's Dialog only renders once openDialog has created its portal
  // target, so an expired session has to open it imperatively — mounting the
  // component alone would leave the blocking dialog invisible. openDialog is
  // a no-op while already open, so re-running this is harmless.
  const { openDialog } = endSessionDialog
  useEffect(() => {
    if (hasSessionExpired) {
      openDialog()
    }
  }, [hasSessionExpired, openDialog])

  // The token still carries the expired session's permissions until refreshed.
  // Tracking expiry here — next to the blocking dialog — is the fitting place
  // to drop them, silently swapping in a fresh token without reloading.
  useEffect(() => {
    if (hasSessionExpired) {
      // Add delay to ensure we refetch after the TBS expired (prevent clock drift shenanigans)
      setTimeout(() => refreshPermissions({ reloadWindow: false }), 1000)
    }
  }, [hasSessionExpired, refreshPermissions])

  const allAreas = navigationSections
    .flatMap(section => section?.areas)
    .filter((area): area is BackofficeAreas[number] => !!area)

  // Track page views for all authenticated routes
  usePageTracking()

  useEffect(() => {
    setRef(appContextRef)
  }, [setRef])

  const [isOffline, setIsOffline] = useState(false)

  const checkNetworkStatus = () => {
    const handleOffline = () => {
      setIsOffline(true)
    }

    const handleOnline = () => {
      setIsOffline(false)
    }
    window.addEventListener('offline', handleOffline)
    window.addEventListener('online', handleOnline)
    return () => {
      window.removeEventListener('offline', handleOffline)
      window.removeEventListener('online', handleOnline)
    }
  }

  // biome-ignore lint/correctness/useExhaustiveDependencies: <auto ignored after biome migration>
  useEffect(() => checkNetworkStatus(), [])

  const claims = useClaims()
  const isSystemUser = claims.boum.isSystemUser()
  const isEmailPasswordUser =
    user?.signInMethod === BackofficeSignInMethod.EmailPassword

  // `null` means the preference could not be read, which is not the same as
  // having no MFA
  if (isEmailPasswordUser && !isSystemUser && hasMFA === false) {
    return <SetupMFA />
  }

  return (
    <>
      <NavigationDrawerContextProvider>
        <AppLayout
          // A running session is a state to stay aware of, an expired one needs
          // ending — the tones the banner explaining it uses.
          attention={
            temporarySession.session
              ? hasSessionExpired
                ? 'critical'
                : 'accent'
              : undefined
          }
          banner={
            <>
              {isOffline && <OfflineBanner />}
              {temporarySession.session && (
                <TemporarySessionBanner
                  session={temporarySession.session}
                  organizationName={temporarySession.organizationName}
                  onProlong={temporarySession.prolong}
                  isProlonging={temporarySession.isProlonging}
                  onEnd={endSessionDialog.openDialog}
                />
              )}
            </>
          }
          navigation={
            <NavigationDrawer
              navigationSections={navigationSections}
              logo={logo}
              logoVariant={logoVariant}
            />
          }
        >
          <Suspense fallback={<AreaLoadingFallback />}>
            <RouterRoutes>
              {!isTemporarySession && (
                <RouterRoute
                  path={AccountViewRoute.route}
                  element={<AccountViewArea />}
                />
              )}
              {allAreas.map(({ id, route }) => (
                <RouterRoute
                  key={id}
                  path={`${route.path}/*`}
                  element={<route.component />}
                />
              ))}
              <RouterRoute path="/" element={<Navigate to={'/welcome'} />} />

              <RouterRoute path="*" element={<PageNotFoundView />} />
            </RouterRoutes>
          </Suspense>
        </AppLayout>
      </NavigationDrawerContextProvider>
      <CommandPalette
        sections={navigationSections}
        getAreaTranslation={getAreaTranslation}
      />
      {/* An expired session forces the dialog open regardless of whether the
          actor opened it: once it runs out they can do nothing else in the
          Backoffice until they end it (or it is closed from the Admin Panel). */}
      {(endSessionDialog.isOpen || hasSessionExpired) &&
        temporarySession.session && (
          <EndSessionDialog
            id={endSessionDialog.id}
            session={temporarySession.session}
            onClose={endSessionDialog.closeDialog}
            // The token still carries the closed session's id and permissions,
            // so the actor has to get a fresh one: signing out drops them back
            // to the login screen, from where they return as themselves.
            onSessionEnded={signOut}
            // Inside the session frame an expired session can no longer be
            // dismissed — the actor must end it.
            blockOnExpiry
          />
        )}
    </>
  )
}
